import {
  APIService,
  ArtButton,
  ArtDialog,
  useApiService,
} from "@art/shared-ui";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ART_URL } from "../../../Constant";

export type ResourceScope = {
  project_key: string;
  project_title?: string;
  environment_name: string;
  environment_title?: string;
};
export type ResourceAccess = {
  email: string;
  status: string;
  resource_scopes: ResourceScope[] | null;
  inherited_resource_scopes?: ResourceScope[] | null;
};
type Option = { label: string; value: string };
const EMPTY_SCOPES: ResourceScope[] = [];

export const accessSummary = (scopes?: ResourceScope[] | null) => {
  const safeScopes = Array.isArray(scopes) ? scopes : [];
  if (!safeScopes.length) return "No resource access";
  if (
    safeScopes.some(
      (scope) => scope.project_key === "*" && scope.environment_name === "*",
    )
  ) {
    return "All projects · All environments";
  }
  const projects = new Set(safeScopes.map((scope) => scope.project_key));
  const environments = new Set(
    safeScopes.map((scope) => scope.environment_name),
  );
  const projectText = projects.has("*")
    ? "All projects"
    : `${projects.size} project${projects.size === 1 ? "" : "s"}`;
  const environmentText = environments.has("*")
    ? "All environments"
    : `${environments.size} environment${environments.size === 1 ? "" : "s"}`;
  return `${projectText} · ${environmentText}`;
};

export const AccessCell = ({
  scopes,
  inheritedScopes,
  status = "loaded",
}: {
  scopes?: ResourceScope[] | null;
  inheritedScopes?: ResourceScope[] | null;
  status?: "loading" | "loaded" | "error";
}) => {
  if (status === "loading")
    return <Chip size="small" variant="outlined" label="Loading access…" />;
  if (status === "error")
    return (
      <Chip
        size="small"
        variant="outlined"
        color="warning"
        label="Access unavailable"
      />
    );
  const safeScopes = Array.isArray(scopes) ? scopes : [];
  const inherited = Array.isArray(inheritedScopes) ? inheritedScopes : [];
  if (!safeScopes.length && !inherited.length) {
    return <Chip size="small" variant="outlined" label="No resource access" />;
  }
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {!!safeScopes.length && (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={`Direct: ${accessSummary(safeScopes)}`}
        />
      )}
      {!!inherited.length && (
        <Chip
          size="small"
          variant="outlined"
          color="secondary"
          label={`Inherited: ${accessSummary(inherited)}`}
        />
      )}
    </Stack>
  );
};

export default function ResourceAccessDialog({
  email,
  open,
  onClose,
  onSaved,
  existingScopes = EMPTY_SCOPES,
}: {
  email: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  existingScopes?: ResourceScope[] | null;
}) {
  const apiService = useApiService();
  const idpService = useMemo(
    () =>
      new APIService({
        ART_STORE: apiService.getStore(),
        BASE_URL: ART_URL + "/idp/api",
        DisableDefaultHeadder: true,
        DisableDefaultTenantUrl: true,
      }),
    [apiService],
  );
  const [projects, setProjects] = useState<Option[]>([]);
  const [environments, setEnvironments] = useState<Option[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Option[]>([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState<Option[]>(
    [],
  );
  const [scopes, setScopes] = useState<ResourceScope[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setLoadError(false);
    setScopes(Array.isArray(existingScopes) ? existingScopes : []);
    setSelectedProjects([]);
    setSelectedEnvironments([]);
    Promise.all([
      apiService.get("/resource-access-catalog/projects"),
      apiService.get("/resource-access-catalog/environments?limit=100"),
    ])
      .then(([projectResponse, environmentResponse]: any[]) => {
        const projectOptions = [
          { label: "All projects", value: "*" },
          ...(projectResponse.data?.data?.projects || []).map((item: any) => ({
            label: item.title,
            value: item.project_key,
          })),
        ];
        const environmentOptions = [
          { label: "All environments", value: "*" },
          ...(environmentResponse.data?.data?.environments || []).map(
            (item: any) => ({ label: item.title, value: item.title }),
          ),
        ];
        setProjects(projectOptions);
        setEnvironments(environmentOptions);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [open, existingScopes]);

  const normalize = (values: Option[]) =>
    values.some((item) => item.value === "*")
      ? [values.find((item) => item.value === "*")!]
      : values;
  const save = async () => {
    if (loadError) return;
    setSaving(true);
    try {
      const projectOptions = normalize(selectedProjects);
      const environmentOptions = normalize(selectedEnvironments);
      const additions = projectOptions.flatMap((project) =>
        environmentOptions.map((environment) => ({
          project_key: project.value,
          project_title: project.label,
          environment_name: environment.value,
          environment_title: environment.label,
        })),
      );
      const nextScopes = additions.some(
        (scope) => scope.project_key === "*" && scope.environment_name === "*",
      )
        ? additions.filter(
            (scope) =>
              scope.project_key === "*" && scope.environment_name === "*",
          )
        : [...scopes, ...additions].filter(
            (scope, index, all) =>
              all.findIndex(
                (item) =>
                  item.project_key === scope.project_key &&
                  item.environment_name === scope.environment_name,
              ) === index,
          );
      await idpService.put("/invites/resource-access", {
        email,
        resource_scopes: nextScopes,
      });
      idpService.showSnack(
        nextScopes.length
          ? "Resource access updated successfully"
          : "Resource access revoked successfully",
        "success",
      );
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ArtDialog
      title={`Manage resource access — ${email}`}
      open={open}
      onClose={onClose}
    >
      <Box sx={{ minWidth: { md: 620 }, p: 1 }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Add or remove direct project and environment grants. Access inherited
          from a role must be changed on that role.
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {loadError && (
              <Alert severity="error">
                Projects or environments could not be loaded. Close this dialog
                and try again; no access changes have been saved.
              </Alert>
            )}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {scopes.length ? (
                scopes.map((scope) => (
                  <Chip
                    key={`${scope.project_key}:${scope.environment_name}`}
                    label={`${scope.project_title || scope.project_key} · ${scope.environment_title || scope.environment_name}`}
                    onDelete={() =>
                      setScopes((current) =>
                        current.filter(
                          (item) =>
                            item.project_key !== scope.project_key ||
                            item.environment_name !== scope.environment_name,
                        ),
                      )
                    }
                  />
                ))
              ) : (
                <Typography color="text.secondary">No direct access</Typography>
              )}
            </Stack>
            <Autocomplete
              multiple
              disabled={loadError}
              options={projects}
              value={selectedProjects}
              onChange={(_, value) => setSelectedProjects(value)}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add projects"
                  placeholder="Select projects"
                />
              )}
            />
            <Autocomplete
              multiple
              disabled={loadError}
              options={environments}
              value={selectedEnvironments}
              onChange={(_, value) => setSelectedEnvironments(value)}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add environments"
                  placeholder="Select environments"
                />
              )}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                pt: 1,
              }}
            >
              <ArtButton
                buttonType="button"
                dataQa="cancel-resource-access"
                label="Cancel"
                variant="outlined"
                onClick={onClose}
              />
              <ArtButton
                buttonType="button"
                dataQa="save-resource-access"
                label={saving ? "Saving…" : "Save access"}
                onClick={save}
                disabled={saving || loadError}
              />
            </Box>
          </Stack>
        )}
      </Box>
    </ArtDialog>
  );
}
